import { useState, useEffect } from "react";
import { Box, Tooltip, ActionIcon, Text, Collapse, Stack, NumberInput, Radio, Group, Divider, Button, Select } from "@mantine/core";
import { IconArrowsExchange } from "@tabler/icons-react";
import { useWebSocket } from "../../WebSocket";

// Joint Settings Panel (expandable inline) - servo/hardware config only
export function JointSettingsPanel({
  joint,
  expanded,
  onToggle,
  mimicSetting,
  mimicTargetOptions,
}: {
  joint: {
    name: string;
    min_angle: number;
    max_angle: number;
    settings: {
      servo_id: number | null;
      direction: 1 | -1;
      angle_offset: number;
      custom_min_angle: number | null;
      custom_max_angle: number | null;
      axis_reversed: boolean;
    } | null;
  };
  expanded: boolean;
  onToggle: () => void;
  mimicSetting?: {
    mimic_joint: string | null;
    mimic_multiplier: number;
    mimic_offset: number;
  };
  mimicTargetOptions: Array<{
    value: string;
    label: string;
  }>;
}) {
  const { sendUpdateJointSettings, sendUpdateMimicSettings, sendSetJointAxisReversed } = useWebSocket();
  const [servoId, setServoId] = useState<number | "">(joint.settings?.servo_id ?? "");
  const [direction, setDirection] = useState<1 | -1>(joint.settings?.direction ?? 1);
  const [angleOffset, setAngleOffset] = useState(joint.settings?.angle_offset ?? 0);
  const [customMinAngle, setCustomMinAngle] = useState<number | "">(
    joint.settings?.custom_min_angle ?? ""
  );
  const [customMaxAngle, setCustomMaxAngle] = useState<number | "">(
    joint.settings?.custom_max_angle ?? ""
  );

  // Sync state when joint changes
  useEffect(() => {
    setServoId(joint.settings?.servo_id ?? "");
    setDirection(joint.settings?.direction ?? 1);
    setAngleOffset(joint.settings?.angle_offset ?? 0);
    setCustomMinAngle(joint.settings?.custom_min_angle ?? "");
    setCustomMaxAngle(joint.settings?.custom_max_angle ?? "");
  }, [joint.settings]);

  const handleSave = () => {
    sendUpdateJointSettings(
      joint.name,
      servoId === "" ? null : servoId,
      direction,
      angleOffset,
      customMinAngle === "" ? null : customMinAngle,
      customMaxAngle === "" ? null : customMaxAngle
    );
  };

  const axisReversed = joint.settings?.axis_reversed ?? false;

  const mimicJoint = mimicSetting?.mimic_joint ?? null;
  const mimicMultiplier = mimicSetting?.mimic_multiplier ?? 1.0;
  const mimicOffset = mimicSetting?.mimic_offset ?? 0.0;

  return (
    <Box>
      <Tooltip label={expanded ? "Hide settings" : "Show settings"}>
        <ActionIcon
          size="xs"
          variant="subtle"
          onClick={onToggle}
        >
          <Text size="xs">⚙</Text>
        </ActionIcon>
      </Tooltip>

      <Collapse in={expanded}>
        <Box
          mt="xs"
          p="xs"
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: 4,
            backgroundColor: "var(--mantine-color-gray-0)",
          }}
        >
          <Stack gap="xs">
            <Divider my={2} label="Mimic" labelPosition="center" />

            <Tooltip label="Mimic target joint (from descendant modules)">
              <Select
                size="xs"
                placeholder="None"
                value={mimicJoint}
                onChange={(value) => {
                  sendUpdateMimicSettings(joint.name, value || null, mimicMultiplier, mimicOffset);
                }}
                data={[
                  { value: "", label: "None" },
                  ...mimicTargetOptions,
                ]}
                clearable
                searchable
                styles={{ input: { fontSize: 11 } }}
              />
            </Tooltip>

            {mimicJoint && (
              <Group gap="xs" grow>
                <Tooltip label="Multiplier (angle = target × mult + offset)">
                  <NumberInput
                    label="Multiplier"
                    size="xs"
                    value={mimicMultiplier}
                    onChange={(value) => {
                      const nextMultiplier = typeof value === "number" ? value : 1.0;
                      sendUpdateMimicSettings(joint.name, mimicJoint, nextMultiplier, mimicOffset);
                    }}
                    step={0.1}
                    decimalScale={2}
                    hideControls
                  />
                </Tooltip>
                <Tooltip label="Offset in radians">
                  <NumberInput
                    label="Offset (rad)"
                    size="xs"
                    value={mimicOffset}
                    onChange={(value) => {
                      const nextOffset = typeof value === "number" ? value : 0.0;
                      sendUpdateMimicSettings(joint.name, mimicJoint, mimicMultiplier, nextOffset);
                    }}
                    step={0.01}
                    decimalScale={3}
                    hideControls
                  />
                </Tooltip>
              </Group>
            )}

            <Divider my={2} label="Axis" labelPosition="center" />

            {/* Reversing the axis changes the joint itself -- the model turns
                the other way and the exported URDF carries a negated axis with
                swapped limits. The servo Direction below is the other thing:
                it leaves the joint alone and reverses only the servo driving
                it. Applies straight away, so there is no Save for it. */}
            <Group justify="space-between" wrap="nowrap">
              <Box>
                <Text size="xs">Rotation direction</Text>
                <Text size="xs" c="dimmed">
                  {axisReversed ? "Reversed from the module" : "As the module defines it"}
                </Text>
              </Box>
              <Tooltip
                label="Negate this joint's axis and swap its limits. The robot does not move; the sign of the command flips. The servo direction and angle offset below flip with it, so a calibrated servo keeps behaving the same. Written into the exported URDF."
                multiline
                w={260}
              >
                <Button
                  size="compact-xs"
                  variant={axisReversed ? "filled" : "default"}
                  color="orange"
                  leftSection={<IconArrowsExchange size={14} />}
                  onClick={() => sendSetJointAxisReversed(joint.name, !axisReversed)}
                >
                  Reverse
                </Button>
              </Tooltip>
            </Group>

            <Divider my={2} label="Hardware" labelPosition="center" />

            <NumberInput
              label="Servo ID"
              placeholder="e.g., 12"
              size="xs"
              value={servoId}
              onChange={(val) => setServoId(typeof val === "number" ? val : "")}
              min={0}
              hideControls
            />

            <Box>
              <Text size="xs" c="dimmed" mb={4}>Direction</Text>
              <Radio.Group
                value={String(direction)}
                onChange={(val) => setDirection(Number(val) as 1 | -1)}
              >
                <Group gap="md">
                  <Radio value="1" label="Normal" size="xs" />
                  <Radio value="-1" label="Reversed" size="xs" />
                </Group>
              </Radio.Group>
            </Box>

            <NumberInput
              label="Angle Offset (rad)"
              size="xs"
              value={angleOffset}
              onChange={(val) => setAngleOffset(typeof val === "number" ? val : 0)}
              step={0.01}
              decimalScale={3}
              hideControls
            />

            <Divider my="xs" label="Angle Limits" labelPosition="center" />

            <Group gap="xs" grow>
              <NumberInput
                label="Min (rad)"
                placeholder={`${joint.min_angle.toFixed(2)}`}
                size="xs"
                value={customMinAngle}
                onChange={(val) => setCustomMinAngle(typeof val === "number" ? val : "")}
                step={0.1}
                decimalScale={2}
                hideControls
              />
              <NumberInput
                label="Max (rad)"
                placeholder={`${joint.max_angle.toFixed(2)}`}
                size="xs"
                value={customMaxAngle}
                onChange={(val) => setCustomMaxAngle(typeof val === "number" ? val : "")}
                step={0.1}
                decimalScale={2}
                hideControls
              />
            </Group>
            <Text size="xs" c="dimmed">
              Leave empty to use URDF defaults
            </Text>

            <Button size="xs" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}
